import { useEffect, useState } from "react";
import {
  Loader2,
  Trash2,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Globe2,
  Clock3,
  KeyRound,
  AlertTriangle,
} from "lucide-react";
import {
  friendlyAuthError,
  listSessions,
  mfaDisable,
  mfaRegenerateRecovery,
  revokeOtherSessions,
  revokeSessionApi,
  type SessionListEntry,
} from "@/lib/api";

export function SecuritySection({
  mfaEnabled,
  mfaRequiredForAll,
  recoveryRemaining,
  onMfaChanged,
}: {
  mfaEnabled: boolean;
  mfaRequiredForAll: boolean;
  recoveryRemaining: number;
  onMfaChanged: () => void;
}) {
  const [sessions, setSessions] = useState<SessionListEntry[]>([]);
  const [currentJti, setCurrentJti] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // MFA management state
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableErr, setDisableErr] = useState<string | null>(null);
  const [disableBusy, setDisableBusy] = useState(false);

  const [showRegen, setShowRegen] = useState(false);
  const [regenCode, setRegenCode] = useState("");
  const [regenCodes, setRegenCodes] = useState<string[] | null>(null);
  const [regenErr, setRegenErr] = useState<string | null>(null);
  const [regenBusy, setRegenBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await listSessions();
      setSessions(r.sessions);
      setCurrentJti(r.current_jti);
      setErr(null);
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function revokeOne(jti: string) {
    setBusy(jti);
    try {
      await revokeSessionApi(jti);
      await load();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(null);
    }
  }

  async function revokeOthers() {
    setBusy("__others");
    try {
      await revokeOtherSessions();
      await load();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(null);
    }
  }

  async function disableMfa(e: React.FormEvent) {
    e.preventDefault();
    setDisableErr(null);
    setDisableBusy(true);
    try {
      await mfaDisable({
        password: disablePassword || undefined,
        code: disableCode || undefined,
      });
      setShowDisable(false);
      setDisablePassword("");
      setDisableCode("");
      onMfaChanged();
    } catch (e: unknown) {
      setDisableErr(friendlyAuthError(e));
    } finally {
      setDisableBusy(false);
    }
  }

  async function regen(e: React.FormEvent) {
    e.preventDefault();
    setRegenErr(null);
    setRegenBusy(true);
    try {
      const r = await mfaRegenerateRecovery(regenCode);
      setRegenCodes(r.recovery_codes);
      setRegenCode("");
      onMfaChanged();
    } catch (e: unknown) {
      setRegenErr(friendlyAuthError(e));
    } finally {
      setRegenBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* MFA panel */}
      <div className="rounded-2xl bg-[var(--color-surface)] p-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--color-accent-soft)] text-[var(--color-accent-hover)]">
            {mfaEnabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldOff className="h-5 w-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-[var(--color-text)]">
              Two-factor authentication
            </h3>
            <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
              {mfaEnabled
                ? `Enabled — ${recoveryRemaining} recovery code${recoveryRemaining === 1 ? "" : "s"} remaining.`
                : mfaRequiredForAll
                  ? "Disabled. Policy will force you to enroll on next sign-in."
                  : "Disabled. Enrolling adds a TOTP step to every login."}
            </p>
          </div>
        </div>
        {mfaEnabled && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowDisable((v) => !v)}
              className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs hover:bg-[var(--color-surface-3)]"
            >
              {showDisable ? "Cancel" : "Disable MFA"}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowRegen((v) => !v);
                setRegenErr(null);
                setRegenCode("");
                setRegenCodes(null);
              }}
              className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs hover:bg-[var(--color-surface-3)]"
              data-test="open-regen"
            >
              {showRegen ? "Cancel recovery regen" : "Regenerate recovery codes"}
            </button>
          </div>
        )}
        {showDisable && (
          <form onSubmit={disableMfa} className="mt-3 grid gap-2 rounded-lg bg-[var(--color-surface-2)] p-3 text-xs">
            <p className="text-[var(--color-text-muted)]">
              Provide your current password OR a fresh authenticator code.
            </p>
            <input
              className="field-input"
              type="password"
              placeholder="Current password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              autoComplete="current-password"
            />
            <input
              className="field-input font-mono tracking-widest"
              inputMode="numeric"
              placeholder="123456"
              maxLength={6}
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              autoComplete="one-time-code"
            />
            {disableErr && <p className="text-[var(--color-danger)]">{disableErr}</p>}
            <button
              type="submit"
              disabled={disableBusy || (!disablePassword && disableCode.length < 6)}
              className="btn btn-primary py-2 text-xs"
            >
              {disableBusy ? <Loader2 className="h-3.5 w-3.5 spin" /> : <ShieldOff className="h-3.5 w-3.5" />}
              Disable MFA
            </button>
          </form>
        )}
        {mfaEnabled && showRegen && (
          <form onSubmit={regen} className="mt-3 grid gap-2 rounded-lg bg-[var(--color-surface-2)] p-3 text-xs">
            <p className="text-[var(--color-text-muted)]">
              Enter a current authenticator code to issue a fresh batch of 10 recovery codes.
              Old codes stop working immediately.
            </p>
            <input
              className="field-input font-mono tracking-widest"
              inputMode="numeric"
              placeholder="123456"
              maxLength={6}
              value={regenCode}
              onChange={(e) => setRegenCode(e.target.value)}
              autoComplete="one-time-code"
            />
            {regenErr && <p className="text-[var(--color-danger)]">{regenErr}</p>}
            <button
              type="submit"
              disabled={regenBusy || regenCode.length < 6}
              className="btn btn-primary py-2 text-xs"
            >
              {regenBusy ? <Loader2 className="h-3.5 w-3.5 spin" /> : <KeyRound className="h-3.5 w-3.5" />}
              Generate new recovery codes
            </button>
            {regenCodes && (
              <div className="mt-2 rounded-lg bg-[var(--color-surface)] p-2">
                <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-[var(--color-warning,_#a86200)]">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Save these codes — shown only once
                </p>
                <ul className="grid grid-cols-2 gap-1 font-mono text-[11px]">
                  {regenCodes.map((c) => (
                    <li key={c} className="rounded bg-[var(--color-surface-2)] px-2 py-1 text-center">
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </form>
        )}
      </div>

      {/* Sessions panel */}
      <div className="rounded-2xl bg-[var(--color-surface)] p-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text)]">Active sessions</h3>
            <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
              Devices and clients currently authenticated. Sessions auto-expire after 30 days.
            </p>
          </div>
          <button
            type="button"
            disabled={!sessions.some((s) => !s.is_current && !s.revoked_at)}
            onClick={revokeOthers}
            className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs hover:bg-[var(--color-surface-3)] disabled:opacity-50"
          >
            Sign out everywhere else
          </button>
        </div>
        {err && (
          <div className="mb-3 flex items-start justify-between gap-3 rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-xs text-[var(--color-danger)]">
            <span>{err}</span>
            <button
              type="button"
              onClick={() => void load()}
              className="shrink-0 rounded-md border border-[rgba(255,59,48,0.35)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-danger)] hover:bg-[rgba(255,59,48,0.08)]"
            >
              Retry
            </button>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-8 text-[var(--color-text-muted)]">
            <Loader2 className="h-5 w-5 spin" />
          </div>
        ) : err ? null : sessions.length === 0 ? (
          <p className="rounded-lg bg-[var(--color-surface-2)] px-3 py-4 text-center text-xs text-[var(--color-text-muted)]">
            No sessions yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li
                key={s.id}
                className="flex flex-wrap items-center gap-3 rounded-xl bg-[var(--color-surface-2)] p-3 text-xs"
              >
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--color-surface)]">
                  <Smartphone className="h-4 w-4 text-[var(--color-text-muted)]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-1.5 font-medium text-[var(--color-text)]">
                    {s.label ?? "Unknown device"}
                    {s.is_current && (
                      <span className="rounded-full bg-[var(--color-accent-soft)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-accent-hover)]">
                        This device
                      </span>
                    )}
                    {s.revoked_at && (
                      <span className="rounded-full bg-[var(--color-danger-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-danger)]">
                        Revoked
                      </span>
                    )}
                    {!s.mfa_verified && (
                      <span className="rounded-full bg-[var(--color-surface-3)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-text-muted)]">
                        MFA pending
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[var(--color-text-muted)]">
                    <span className="flex items-center gap-1">
                      <Globe2 className="h-3 w-3" />
                      {s.ip ?? "—"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock3 className="h-3 w-3" />
                      Last seen {new Date(s.last_seen_at).toLocaleString()}
                    </span>
                  </p>
                  {s.user_agent && (
                    <p className="mt-1 truncate text-[10px] text-[var(--color-text-subtle)]">
                      {s.user_agent}
                    </p>
                  )}
                </div>
                {!s.revoked_at && (
                  <button
                    type="button"
                    disabled={busy === s.token_jti || (s.is_current && currentJti !== null)}
                    onClick={() => revokeOne(s.token_jti)}
                    className="flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-2 py-1 text-[11px] hover:bg-[var(--color-surface-3)] disabled:opacity-50"
                    title={s.is_current ? "Use 'Sign out' from the sidebar to end this device's session" : "Revoke session"}
                  >
                    {busy === s.token_jti ? (
                      <Loader2 className="h-3 w-3 spin" />
                    ) : (
                      <Trash2 className="h-3 w-3" />
                    )}
                    Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
